// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * React hook for fetching inventory data for shipment dialogs.
 * This module provides functionality for fetching all inventory at a single location
 * and returning it in a Map structure for efficient lookups by item ID.
 */
import { AssetFunctionalStatus } from '@/constants/asset-functional-status';
import { useAssetsInventory } from '@/inventory/hooks/use-assets-inventory';
import { useItemInventory } from '@/inventory/hooks/use-item-inventory';
import {
  buildLocationInventoryMap,
  toStockedBulkItems,
} from '@/inventory/utilities/build-location-inventory-map';
import { toSiteId } from '@/location/utilities/to-site-id';
import { SparseLocation, SparseLocationWithParent } from '@/sparse/api/types';
import { useMemo } from 'react';

/** The status an asset must have to ship. */
const FUNCTIONAL_STATUS: AssetFunctionalStatus = 'FUNCTIONAL';

/**
 * Configuration options for the useShipmentInventory hook.
 */
interface UseShipmentInventoryOptions {
  location: SparseLocation | SparseLocationWithParent | null | undefined;
}

/**
 * Return type for the useShipmentInventory hook.
 */
interface UseShipmentInventoryResult {
  /** Map structure: locationId -> itemId -> quantity, keyed by each exact site or zone */
  inventoryMap: Map<string, Map<string, number>>;
  /** Map structure: locationId -> productId -> array of serials, keyed by each exact site or zone */
  serialsMap: Map<string, Map<string, string[]>>;
  /** True if any inventory query is loading for the first time */
  isLoading: boolean;
  /** True if any inventory query is fetching (including background refetches) */
  isFetching: boolean;
}

/**
 * A React hook that fetches the inventory at a location's site for use in shipment dialogs.
 *
 * This hook:
 * - Fetches the bulk items and assets at the site, so every zone of a site shares one cached query
 * - Keys its maps by each exact site or zone, so a lookup reads only the selected location
 * - With staleTime: 0, always fetches fresh data on mount
 * - Auto-refetches every 30 seconds while active
 *
 * @param options - Configuration object with the selected location
 * @returns Object containing inventory maps, loading states
 *
 * @example
 * ```tsx
 * const { inventoryMap, serialsMap, isLoading } = useShipmentInventory({
 *   location: shipWarehouse
 * });
 *
 * // Look up available quantity for an item
 * const availableQty = inventoryMap.get(locationId)?.get(itemId) ?? 0;
 *
 * // Look up available serials for a product
 * const availableSerials = serialsMap.get(locationId)?.get(productId) ?? [];
 * ```
 */
export function useShipmentInventory(
  options: UseShipmentInventoryOptions
): UseShipmentInventoryResult {
  const siteId = options.location ? toSiteId(options.location) : undefined;

  const itemInventory = useItemInventory({
    filters: {
      locationIds: siteId ? [siteId] : undefined,
    },
    enabled: !!siteId,
  });

  const assetsInventory = useAssetsInventory({
    filters: {
      locationId: siteId ? [siteId] : undefined,
    },
    enabled: !!siteId,
  });

  const inventoryMap = useMemo(() => {
    const functionalGroups =
      assetsInventory.assetsInventory?.data.results.filter(
        (group) => group.functionalStatus === FUNCTIONAL_STATUS
      ) ?? [];
    return buildLocationInventoryMap([
      ...toStockedBulkItems(itemInventory.itemInventory?.data.results ?? []),
      ...functionalGroups,
    ]);
  }, [itemInventory.itemInventory, assetsInventory.assetsInventory]);

  const serialsMap = useMemo(() => {
    const map = new Map<string, Map<string, string[]>>();
    for (const group of assetsInventory.assetsInventory?.data.results ?? []) {
      if (group.functionalStatus !== FUNCTIONAL_STATUS) {
        continue;
      }

      for (const entry of group.inventory) {
        const locationMap =
          map.get(entry.location.id) ?? new Map<string, string[]>();
        locationMap.set(group.item.id, [
          ...(locationMap.get(group.item.id) ?? []),
          ...entry.serials,
        ]);
        map.set(entry.location.id, locationMap);
      }
    }

    return map;
  }, [assetsInventory.assetsInventory]);

  return {
    inventoryMap,
    serialsMap,
    isLoading: itemInventory.isLoading || assetsInventory.isLoading,
    isFetching: itemInventory.isFetching || assetsInventory.isFetching,
  };
}
